import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@/hooks/auth-context';
import { clearToasts } from '@/hooks/useToast';
import { supabase } from '@/lib/supabase';
import type { TrainerProfile } from '@/types';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication provider component
 * @param children - Child components to wrap
 */
export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [trainerProfile, setTrainerProfile] = useState<TrainerProfile | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const userIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Cached queries, the profile, and open toasts belong to the signed-in trainer.
    // Drop them when that trainer signs out or another account takes over, but not on token refresh.
    function trackUser(session: Session | null): void {
      const nextUserId = session?.user?.id ?? null;
      if (nextUserId === userIdRef.current) return;
      if (userIdRef.current !== null) {
        queryClient.clear();
        setTrainerProfile(null);
        clearToasts();
      }
      userIdRef.current = nextUserId;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      trackUser(session);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchTrainerProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      trackUser(session);
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchTrainerProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  async function fetchTrainerProfile(userId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('trainer_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      // A different account signed in while this was loading.
      if (userIdRef.current !== userId) return;
      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching trainer profile:', error);
      }
      setTrainerProfile(data);
    } finally {
      if (userIdRef.current === userId) setLoading(false);
    }
  }

  async function signIn(email: string, password: string): Promise<void> {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  }

  async function requestPasswordReset(
    email: string,
    redirectTo?: string
  ): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) throw error;
  }

  async function updatePassword(password: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }

  async function signUp(
    email: string,
    password: string,
    professionalType: string,
    emailRedirectTo: string
  ): Promise<{ signedIn: boolean }> {
    // The auth.users signup trigger creates the trainer profile from this metadata.
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { professional_type: professionalType },
        emailRedirectTo,
      },
    });
    if (error) throw error;
    return { signedIn: data.session !== null };
  }

  async function signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        trainerProfile,
        loading,
        signIn,
        requestPasswordReset,
        signUp,
        updatePassword,
        signOut,
        getCurrentUserId: () => userIdRef.current,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
